import type { QueryClient } from "@tanstack/react-query";
import type {
  GetNotificationResponse,
  PaginatedResultOfGetNotificationResponse,
} from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";

export const normalizeNotificationId = (id: unknown) => String(id).toLowerCase();

export const getMarkedReadQueryKey = (userId: number) =>
  [QueryKeys.NotificationsMarkedRead, userId] as const;

export function resolveNotificationIsRead(
  notification: Pick<GetNotificationResponse, "id" | "isRead">,
  markedReadIds: Set<string> | string[],
): boolean {
  const marked =
    markedReadIds instanceof Set
      ? markedReadIds
      : new Set(markedReadIds.map(normalizeNotificationId));

  const id = normalizeNotificationId(notification.id);
  if (marked.has(id)) return true;
  return notification.isRead === true;
}

export function isNotificationUnread(
  notification: Pick<GetNotificationResponse, "id" | "isRead">,
  markedReadIds: Set<string> | string[],
): boolean {
  return !resolveNotificationIsRead(notification, markedReadIds);
}

export const applyMarkAsReadToNotificationItems = (
  items: GetNotificationResponse[] | undefined,
  notificationIds: string[],
): GetNotificationResponse[] => {
  if (!items?.length) return items ?? [];

  const idsSet = new Set(notificationIds.map(normalizeNotificationId));
  const now = new Date();

  return items.map((notification) =>
    idsSet.has(normalizeNotificationId(notification.id))
      ? {
          ...notification,
          isRead: true,
          readAt: notification.readAt ?? now,
        }
      : notification,
  );
};

export const applyMarkAsReadToUnreadCache = (
  old: PaginatedResultOfGetNotificationResponse | undefined,
  notificationIds: string[],
): PaginatedResultOfGetNotificationResponse | undefined => {
  if (!old) return old;

  const idsSet = new Set(notificationIds.map(normalizeNotificationId));
  const updatedItems = applyMarkAsReadToNotificationItems(
    old.items ?? [],
    notificationIds,
  );

  const removedUnreadCount = (old.items ?? []).filter(
    (notification) =>
      idsSet.has(normalizeNotificationId(notification.id)) &&
      !notification.isRead,
  ).length;

  const unreadItems = updatedItems.filter(
    (notification) => !notification.isRead,
  );

  return {
    ...old,
    items: unreadItems,
    totalCount: Math.max(0, Number(old.totalCount ?? 0) - removedUnreadCount),
  };
};

export const applyMarkAsReadToNotificationsCache = (
  old:
    | PaginatedResultOfGetNotificationResponse
    | GetNotificationResponse[]
    | undefined,
  notificationIds: string[],
): PaginatedResultOfGetNotificationResponse | GetNotificationResponse[] | undefined => {
  if (!old) return old;

  // Parent inbox caches the full list as an array. Spreading that array
  // turns it into an object and the table crashes on the next render.
  if (Array.isArray(old)) {
    return applyMarkAsReadToNotificationItems(old, notificationIds);
  }

  return {
    ...old,
    items: applyMarkAsReadToNotificationItems(old.items ?? [], notificationIds),
  };
};

export const addMarkedReadIds = (
  queryClient: QueryClient,
  userId: number,
  notificationIds: string[],
) => {
  if (!userId || notificationIds.length === 0) return;

  queryClient.setQueryData<string[]>(
    getMarkedReadQueryKey(userId),
    (old = []) => [
      ...new Set([
        ...old.map(normalizeNotificationId),
        ...notificationIds.map(normalizeNotificationId),
      ]),
    ],
  );
};

export const getMarkedReadIds = (
  queryClient: QueryClient,
  userId: number,
): string[] => {
  return (
    queryClient.getQueryData<string[]>(getMarkedReadQueryKey(userId)) ?? []
  );
};

export const syncMarkedReadAfterServerRefetch = (
  queryClient: QueryClient,
  userId: number,
) => {
  const marked = getMarkedReadIds(queryClient, userId);
  if (marked.length === 0) return;

  const serverReadIds = new Set<string>();

  const queries = [
    ...queryClient.getQueriesData<PaginatedResultOfGetNotificationResponse>({
      queryKey: [QueryKeys.NotificationsUnread, userId],
    }),
    ...queryClient.getQueriesData<PaginatedResultOfGetNotificationResponse>({
      queryKey: [QueryKeys.Notifications, userId],
    }),
  ];

  for (const [, data] of queries) {
    for (const item of itemsFromNotificationsCacheData(data)) {
      if (item.isRead) {
        serverReadIds.add(normalizeNotificationId(item.id));
      }
    }
  }

  const pending = marked.filter(
    (id) => !serverReadIds.has(normalizeNotificationId(id)),
  );

  queryClient.setQueryData(getMarkedReadQueryKey(userId), pending);
};

/** @deprecated Use syncMarkedReadAfterServerRefetch after a server refetch. */
export const pruneConfirmedMarkedReadIds = syncMarkedReadAfterServerRefetch;

export const applyDeleteToNotificationItems = (
  items: GetNotificationResponse[] | undefined,
  notificationIds: string[],
): GetNotificationResponse[] => {
  if (!items?.length) return items ?? [];

  const idsSet = new Set(notificationIds.map(normalizeNotificationId));
  return items.filter(
    (notification) => !idsSet.has(normalizeNotificationId(notification.id)),
  );
};

export const applyDeleteToNotificationsCache = (
  old:
    | PaginatedResultOfGetNotificationResponse
    | GetNotificationResponse[]
    | undefined,
  notificationIds: string[],
): PaginatedResultOfGetNotificationResponse | GetNotificationResponse[] | undefined => {
  if (!old) return old;

  if (Array.isArray(old)) {
    return applyDeleteToNotificationItems(old, notificationIds);
  }

  const idsSet = new Set(notificationIds.map(normalizeNotificationId));
  const previousItems = old.items ?? [];
  const items = applyDeleteToNotificationItems(previousItems, notificationIds);
  const removedCount = previousItems.filter((notification) =>
    idsSet.has(normalizeNotificationId(notification.id)),
  ).length;

  return {
    ...old,
    items,
    totalCount: Math.max(0, Number(old.totalCount ?? 0) - removedCount),
  };
};

export const applyDeleteToUnreadCache = (
  old: PaginatedResultOfGetNotificationResponse | undefined,
  notificationIds: string[],
): PaginatedResultOfGetNotificationResponse | undefined => {
  if (!old) return old;

  const idsSet = new Set(notificationIds.map(normalizeNotificationId));
  const previousItems = old.items ?? [];
  const items = applyDeleteToNotificationItems(previousItems, notificationIds);
  const removedUnreadCount = previousItems.filter(
    (notification) =>
      idsSet.has(normalizeNotificationId(notification.id)) &&
      !notification.isRead,
  ).length;

  return {
    ...old,
    items,
    totalCount: Math.max(0, Number(old.totalCount ?? 0) - removedUnreadCount),
  };
};

export const countUnreadNotifications = (
  data: PaginatedResultOfGetNotificationResponse | undefined,
  markedReadIds: string[],
): number => {
  const items = data?.items ?? [];
  if (!items.length) return 0;

  const unreadCount = items.filter((notification) =>
    isNotificationUnread(notification, markedReadIds),
  ).length;

  const totalCount = Number(data?.totalCount ?? items.length);
  if (items.length >= totalCount) {
    return unreadCount;
  }

  return unreadCount;
};

export const getEffectiveUnreadCount = (
  unreadData: PaginatedResultOfGetNotificationResponse | undefined,
  markedReadIds: string[],
): number => {
  const items = unreadData?.items ?? [];
  const marked = new Set(markedReadIds.map(normalizeNotificationId));

  if (items.length === 0) {
    return Math.max(0, Number(unreadData?.totalCount ?? 0));
  }

  const unreadOnPage = items.filter((notification) =>
    isNotificationUnread(notification, markedReadIds),
  ).length;

  const hasReadItemsOnPage = items.some((notification) =>
    resolveNotificationIsRead(notification, marked),
  );

  if (hasReadItemsOnPage) {
    return unreadOnPage;
  }

  const totalCount = Math.max(0, Number(unreadData?.totalCount ?? unreadOnPage));
  const markedOnPage = items.filter((notification) =>
    marked.has(normalizeNotificationId(notification.id)),
  ).length;

  return Math.max(0, totalCount - markedOnPage);
};

export const filterUnreadNotifications = (
  items: GetNotificationResponse[] | undefined,
  markedReadIds: string[],
): GetNotificationResponse[] => {
  if (!items?.length) return [];

  return items.filter((notification) =>
    isNotificationUnread(notification, markedReadIds),
  );
};

function notificationQueryUserId(
  queryKey: readonly unknown[],
): number | null {
  if (queryKey[0] !== QueryKeys.Notifications) return null;
  if (queryKey[1] === "scroll" || queryKey[1] === "full-list") {
    const id = Number(queryKey[2]);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  const id = Number(queryKey[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function itemsFromNotificationsCacheData(
  data: unknown,
): GetNotificationResponse[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.filter(
      (item): item is GetNotificationResponse =>
        item != null && typeof item === "object" && "id" in item,
    );
  }
  if (
    typeof data === "object" &&
    "items" in data &&
    Array.isArray((data as PaginatedResultOfGetNotificationResponse).items)
  ) {
    return (data as PaginatedResultOfGetNotificationResponse).items ?? [];
  }
  return [];
}

/**
 * Inbox GET `/notifications` rows from cache. That payload's `isRead` is
 * trustworthy; `/unread` often is not.
 */
export function collectInboxNotificationsFromCache(
  queryClient: QueryClient,
  userId: number,
): GetNotificationResponse[] {
  if (!userId) return [];

  const byId = new Map<string, GetNotificationResponse>();
  const queries = queryClient.getQueriesData({
    queryKey: [QueryKeys.Notifications],
  });

  for (const [queryKey, data] of queries) {
    if (notificationQueryUserId(queryKey) !== userId) continue;
    for (const item of itemsFromNotificationsCacheData(data)) {
      byId.set(normalizeNotificationId(item.id), item);
    }
  }

  return [...byId.values()];
}
